import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { utils as koilibUtils } from "koilib";
import { get as _get } from "lodash";
import { BigNumber } from "bignumber.js";
import { Alert, Box, Button, Card, CardHeader, CircularProgress, Typography, Chip, Modal } from "@mui/material";

// constants
import { pools as ListPools } from "../../constants/earn";

// icons
import { useWallet } from "../../hooks/useWallet";

// components
import FiltersEarn from "../../components/FiltersEarn";
import EarnTable from "../../components/EarnTable";
import { formatAmount } from "../../utils/format";
import StakingComponent from "../../components/Staking";
import TokenLogo from "../../components/TokenLogo";

// Utils

// contracts
import { StakingContract } from "../../helpers/contracts";
import { mapSettledWithLimit } from "../../utils/async";

const REFRESH_TIMING = 30 * 1000;
// staking contracts read at the same time (public RPC nodes rate limit bursts)
const CONTRACT_CONCURRENCY = 4;

const PoolsPage = () => {
  // Hooks

  // components state
  const [stake, setStake] = useState(null);

  // filters state
  const [filterStake, setFilterStake] = useState(false);
  const [filterStatus, setFilterStatus] = useState("Live");
  const [filterSearch, setFilterSearch] = useState(null);

  // selectors
  const headInfo = useSelector((state) => state.blockchain.headInfo);
  const walletSelector = useWallet();
  const base_tokens = useSelector((state) => state.tokens.base_tokens);
  const custom_tokens = useSelector((state) => state.tokens.custom_tokens);

  // variables
  const provider = _get(walletSelector, "provider", null);
  const signer = _get(walletSelector, "signer", null);

  // contract data of every pool, read in parallel; a pool that fails is left out
  let loadPool = async () => {
    let tokenList = base_tokens.concat(custom_tokens);
    let results = await mapSettledWithLimit(ListPools, CONTRACT_CONCURRENCY, async (_pool) => {
      try {
        let pool = (await StakingContract(_pool.address, provider, signer).functions.get_pool({})).result;
        return {
          ...pool,
          ..._pool,
          tokenReward: tokenList.find((token) => token.address === _get(pool, "tokenReward", "")),
          tokenDeposit: tokenList.find((token) => token.address === "koin"),
          totalEarn: "0",
          balance: "0",
          totalLiquidity: "0",
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    });
    return results.map((result) => result.value).filter(Boolean);
  };

  // wallet balance and pending reward, without a wallet the pools are listed with zeros
  let loadValues = async (poolsbase = []) => {
    let owner = _get(walletSelector, "wallet[0].address", null);
    let results = await mapSettledWithLimit(poolsbase, CONTRACT_CONCURRENCY, async (_pool) => {
      let balance = null;
      let totalEarn = null;
      if (owner) {
        try {
          let _contractStaking = StakingContract(_pool.address, provider, signer);
          [balance, totalEarn] = await Promise.all([
            _contractStaking.functions.balance_of({ owner }).then(({ result }) => result),
            _contractStaking.functions.get_pending_reward({ owner }).then(({ result }) => result),
          ]);
        } catch (error) {
          console.log(error);
          return null;
        }
      }
      return {
        ..._pool,
        balance: balance ? balance.value : "0",
        totalEarn: totalEarn ? totalEarn.value : "0",
      };
    });
    return results.map((result) => result.value).filter(Boolean);
  };

  const applyFilters = (_pools) => {
    let result = _pools;

    // filter stake
    if (filterStake) {
      result = result.filter((pool) => pool.balance != "0");
    }

    // filter status
    if (filterStatus == "Finished") {
      let blockHead = new BigNumber(_get(headInfo, "head_topology.height", "0"));
      result = result.filter((pool) => {
        let blockEnd = new BigNumber(_get(pool, "blockEnd", "0"));
        if (blockHead.isGreaterThan(blockEnd)) {
          return pool;
        }
      });
    }

    return result;
  };

  // queries: the contract data rarely changes, the wallet values refresh every 30 s while the tab is visible
  const tokensCount = base_tokens.concat(custom_tokens).length;
  const walletAddress = _get(walletSelector, "wallet[0].address", null);
  const poolsQuery = useQuery({
    queryKey: ["pools", "contracts", tokensCount],
    queryFn: () => loadPool(),
    enabled: tokensCount > 0,
    staleTime: 5 * 60 * 1000,
  });
  const valuesQuery = useQuery({
    queryKey: ["pools", "values", walletAddress, tokensCount],
    queryFn: () => loadValues(poolsQuery.data),
    enabled: Boolean(poolsQuery.data),
    refetchInterval: REFRESH_TIMING,
  });
  const pools = valuesQuery.data || [];
  const loading = !valuesQuery.data;

  const loadError = poolsQuery.isError || valuesQuery.isError;
  const retry = () => (poolsQuery.isError ? poolsQuery.refetch() : valuesQuery.refetch());

  if (loading) {
    return (
      <Card
        sx={{
          padding: "1em",
          maxWidth: "400px",
          marginX: "auto",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <CardHeader title={"Pools"} />
        {loadError ? (
          <>
            <Alert variant="outlined" severity="error" sx={{ color: "text.main", marginY: "1em" }}>
              We could not load the pools.
            </Alert>
            <Button
              variant="contained"
              sx={{ borderRadius: "10px" }}
              onClick={retry}
              disabled={poolsQuery.isFetching || valuesQuery.isFetching}
            >
              Retry
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ textAlign: "center", marginTop: "20px" }}>
              <CircularProgress sx={{ marginBottom: "40px" }} />
            </Box>
            <Typography>We are loading the pools...</Typography>
          </>
        )}
      </Card>
    );
  }

  const getImageToken = (token) => <TokenLogo token={token} size={30} />;

  const getStatus = (el) => {
    let blockHead = new BigNumber(_get(headInfo, "head_topology.height", "0"));
    let blockStart = new BigNumber(_get(el, "blockStart", "0"));
    let blockEnd = new BigNumber(_get(el, "blockEnd", "0"));
    if (blockHead.isLessThan(blockStart)) {
      return <Chip size="small" variant="outlined" label="Waiting" color="info" />;
    }
    if (blockHead.isGreaterThan(blockEnd)) {
      return <Chip size="small" variant="outlined" label="Finished" color="error" />;
    }
    return <Chip size="small" variant="outlined" label="Live" color="primary" />;
  };

  // pool rows for the table
  const rows = applyFilters(pools).map((el, index) => {
    const decimals = _get(el, "tokenDeposit.decimals", "0");
    const depositSymbol = _get(el, "tokenDeposit.symbol", "");
    return {
      key: _get(el, "address", index),
      tokens: [_get(el, "tokenDeposit", null), _get(el, "tokenReward", null)],
      name: `${depositSymbol}-${_get(el, "tokenReward.symbol", "")}`,
      status: getStatus(el),
      earned: `${formatAmount(koilibUtils.formatUnits(_get(el, "totalEarn", "0") || "0", decimals), { maxDecimals: 6 })} ${_get(el, "tokenReward.symbol", "")}`,
      staked: `${formatAmount(koilibUtils.formatUnits(_get(el, "balance", "0") || "0", decimals), { maxDecimals: 6 })} ${depositSymbol}`,
      total: `${formatAmount(koilibUtils.formatUnits(_get(el, "supplyDeposit", "0") || "0", decimals), { maxDecimals: 6 })} ${depositSymbol}`,
      onOpen: () => setStake(el),
    };
  });

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <Card>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <FiltersEarn
            name="Pools"
            search="Search pool..."
            enableStake={true}
            filterStake={filterStake}
            setFilterStake={setFilterStake}
            enableStatus={true}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            enableSearch={false}
            filterSearch={filterSearch}
            setFilterSearch={setFilterSearch}
          />
        </Box>
        <EarnTable
          rows={rows}
          nameLabel="Pool"
          emptyText={pools.length ? "No pools match these filters." : "There are no pools yet."}
        />
      </Card>
      <Modal
        open={!!stake}
        onClose={() => setStake(null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ display: "flex", p: 2 }}
      >
        <Box sx={{ width: "100%", maxWidth: 480, m: "auto" }}>
          <StakingComponent
            type={"pool"}
            name={`${_get(pools[0], "tokenDeposit.symbol", "")}`}
            image={getImageToken(_get(pools[0], "tokenDeposit", null))}
            onClose={() => setStake(null)}
            {...pools[0]}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default PoolsPage;
